import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { acceptInvitation, declineInvitation } from '../../services/groupService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const AcceptInvitation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, []);

  const loadInvitation = async () => {
    const token = searchParams?.get('token');
    
    if (!token) {
      setError('Invalid invitation link');
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase?.from('group_invitations')?.select(`
          *,
          trip_groups(
            name,
            description
          )
        `)?.eq('invitation_token', token)?.single();

      if (error) throw error;

      if (!data) {
        setError('Invitation not found');
        setIsLoading(false);
        return;
      }

      // Check if expired
      if (new Date(data?.expires_at) < new Date()) {
        setError('This invitation has expired');
        setIsLoading(false);
        return;
      }

      // Check if already accepted or declined
      if (data?.status !== 'pending') {
        setError(`This invitation has already been ${data?.status}`);
        setIsLoading(false);
        return;
      }

      setInvitation({
        id: data?.id,
        groupName: data?.trip_groups?.name,
        groupDescription: data?.trip_groups?.description,
        role: data?.role,
        inviteeEmail: data?.invitee_email
      });
    } catch (error) {
      console.error('Error loading invitation:', error);
      setError('Failed to load invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user?.id) {
      alert('Please sign in to accept this invitation');
      navigate('/login');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await acceptInvitation(invitation?.id, user?.id);
      if (result?.success) {
        alert('Invitation accepted! Redirecting to your dashboard...');
        navigate('/user-dashboard');
      } else {
        alert('Failed to accept invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    try {
      const result = await declineInvitation(invitation?.id);
      if (result?.success) {
        alert('Invitation declined.');
        navigate('/');
      } else {
        alert('Failed to decline invitation.');
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Failed to decline invitation.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-20 pb-12">
        <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-card rounded-xl border border-border shadow-lg overflow-hidden">
            {isLoading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading invitation...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} className="text-destructive" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Invalid Invitation</h2>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/')}
                  iconName="Home"
                  iconPosition="left"
                >
                  Go to Homepage
                </Button>
              </div>
            ) : invitation ? (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-secondary p-8 text-white text-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                    <Icon name="Mail" size={32} />
                  </div>
                  <h1 className="text-3xl font-bold mb-2">Group Invitation</h1>
                  <p className="text-white/90">You have been invited to join a travel group</p>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="bg-muted/50 rounded-lg p-6 mb-6">
                    <h2 className="text-xl font-bold text-foreground mb-2">
                      {invitation?.groupName}
                    </h2>
                    {invitation?.groupDescription && (
                      <p className="text-muted-foreground mb-4">
                        {invitation?.groupDescription}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Mail" size={16} className="text-primary" />
                      <span className="text-foreground">Invited as: {invitation?.inviteeEmail}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm mt-2">
                      <Icon name="Shield" size={16} className="text-secondary" />
                      <span className="text-foreground capitalize">Role: {invitation?.role}</span>
                    </div>
                  </div>

                  {/* Role Description */}
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="Info" size={16} className="text-accent" />
                      What you can do as a {invitation?.role}
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {invitation?.role === 'owner' && (
                        <>
                          <li>• Full access to manage the group</li>
                          <li>• Add and remove members</li>
                          <li>• Edit itinerary and expenses</li>
                          <li>• Delete the group</li>
                        </>
                      )}
                      {invitation?.role === 'editor' && (
                        <>
                          <li>• View and edit the itinerary</li>
                          <li>• Add and manage expenses</li>
                          <li>• Invite other members</li>
                        </>
                      )}
                      {invitation?.role === 'viewer' && (
                        <>
                          <li>• View the itinerary</li>
                          <li>• View group expenses</li>
                          <li>• Track your share of costs</li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Actions */}
                  {!user ? (
                    <div className="text-center">
                      <p className="text-muted-foreground mb-4">
                        Please sign in to accept this invitation
                      </p>
                      <Button
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={() => navigate('/login')}
                        iconName="LogIn"
                        iconPosition="left"
                      >
                        Sign In
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        size="lg"
                        fullWidth
                        onClick={handleDecline}
                        disabled={isProcessing}
                        iconName="X"
                        iconPosition="left"
                      >
                        Decline
                      </Button>
                      <Button
                        variant="default"
                        size="lg"
                        fullWidth
                        onClick={handleAccept}
                        loading={isProcessing}
                        disabled={isProcessing}
                        iconName="Check"
                        iconPosition="left"
                      >
                        Accept Invitation
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;